//@flow
import React from 'react';
import { connect } from 'react-redux';
import { Box } from '@locus-taxy/neo-ui';
import { Dialog } from 'common/components/core/dialog';
import theme from 'common/components/core/ui_switcher/UiSwitcherPopup.scss';
import AppPreferencesActions from 'common/redux/actions/appPreferences';
import UISwitcherActions from 'common/redux/actions/uiSwitcher';
import { translate, type TFunction } from 'react-i18next';
import { getV2UIStatus } from 'common/redux/selectors/appPreferences';
import {
    getV2PopupState,
    getIsAccessWalkthroughAcknowledged,
    getIsV1PopupAcknowledged
} from 'common/redux/selectors/uiSwitcher';
import { getLiveViewVisibilitySettings } from 'common/redux/selectors/clientSelector';
import { isGlobalPersonnelSelector } from 'common/redux/selectors/sessionSelector';
import { UISwitcherStateEnum as UI_SWICHER_STATES } from 'common/enums/UISwitcher';
import Products, { type ProductType } from 'common/enums/Product';
import UiSwitcherIds from 'common/tests/common/UiSwitcher.json';
import type { LiveViewVisibilitySettingsModelType } from 'common/models/flow/Client';

type OwnProps = {
    onDismiss: () => void,
    onUpgrade?: () => void,
    onRevert?: () => void,
    product: ProductType
};

type Props = {
    ...OwnProps,
    isGlobalPersonnel: boolean,
    isV2UIActive: boolean,
    v2PopupState: string | null,
    showV2UI: (enableStatus: boolean, product: ProductType) => void,
    updateV2PopupState: (popupState: string, product: ProductType) => void,
    t: TFunction,
    isAccessWalkthroughAcknowledged: boolean,
    acknowledgeV2Popup: (product: ProductType) => void,
    acknowledgeV1Popup: (product: ProductType) => void,
    acknowledgeAccessWalkthrough: (product: ProductType) => void,
    liveViewVisibilitySettings: LiveViewVisibilitySettingsModelType
};

type IUpgradePopupState = {
    disableAccept?: boolean,
    acceptActionLabel: string | null,
    cancelActionLabel: string,
    title: string,
    onAccept: () => void,
    onCancel: () => void,
    onDismiss: () => void,
    Content: React$ComponentType<{}>
};

type State = {};

const getPopupTexts = (
    product: ProductType,
    t: TFunction,
    liveViewVisibilitySettings: ?LiveViewVisibilitySettingsModelType,
    isGlobalPersonnel?: boolean
) => {
    const defaultTexts = {
        [UI_SWICHER_STATES.NEW_UI]:
            product === Products.DISPATCHER_PLANNER && !isGlobalPersonnel
                ? {
                      acceptActionLabel: t('v2UiUpgradePopup.acceptTour'),
                      cancelActionLabel: t('v2UiUpgradePopup.rejectTour'),
                      title: t('v2UiUpgradePopup.welcomeToPlanningTitle'),
                      content: [t('v2UiUpgradePopup.takeAVirtualTourDesc')]
                  }
                : {
                      acceptActionLabel: t('v2UiUpgradePopup.tour'),
                      cancelActionLabel: t('v2UiUpgradePopup.revert'),
                      title: t('v2UiUpgradePopup.welcomeToPlanningTitle'),
                      content: [t('v2UiUpgradePopup.takeAVirtualTourDesc')]
                  },
        [UI_SWICHER_STATES.MANUALLY_NAVIGATED_TO_OLD_UI]: {
            acceptActionLabel: t('v2UiUpgradePopup.openNewView'),
            cancelActionLabel: t('v2UiUpgradePopup.revert'),
            title: t('v2UiUpgradePopup.newExperience'),
            content: [t('v2UiUpgradePopup.newUIDescription')]
        },
        [UI_SWICHER_STATES.OLD_UI]: {
            acceptActionLabel: t('v2UiUpgradePopup.upgrade'),
            cancelActionLabel: t('v2UiUpgradePopup.later'),
            title: t('v2UiUpgradePopup.announcement'),
            content: [
                t('v2UiUpgradePopup.announcementText1'),
                t('v2UiUpgradePopup.announcementText2')
            ]
        },
        [UI_SWICHER_STATES.JUST_REVERTED]: {
            acceptActionLabel: t('v2UiUpgradePopup.upgrade'),
            cancelActionLabel: t('v2UiUpgradePopup.dismiss'),
            title: t('v2UiUpgradePopup.upgradeLater'),
            content: [t('v2UiUpgradePopup.upgradeLaterDescription')]
        },
        [UI_SWICHER_STATES.ACCESS_WALKTHROUGH_ANYTIME]: {
            acceptActionLabel: null,
            cancelActionLabel: t('v2UiUpgradePopup.dismiss'),
            title: t('v2UiUpgradePopup.walkthroughTitle'),
            content: [t('v2UiUpgradePopup.walkthroughDescription')]
        }
    };

    return defaultTexts;
};
class UiSwitcherPopup extends React.Component<Props, State> {
    componentDidMount() {
        const {
            v2PopupState,
            isV2UIActive,
            updateV2PopupState,
            product
        } = this.props;
        let popupState;

        if (!v2PopupState) {
            return;
        }
        if (
            v2PopupState === UI_SWICHER_STATES.JUST_REVERTED ||
            /* To handle cases where user has chosen to upgrade but navigated
        to old view through a bookmarked URL pointing to old view */
            v2PopupState === UI_SWICHER_STATES.MANUALLY_NAVIGATED_TO_OLD_UI
        ) {
            popupState = v2PopupState;
        } else {
            popupState = isV2UIActive
                ? UI_SWICHER_STATES.NEW_UI
                : UI_SWICHER_STATES.OLD_UI;
        }
        updateV2PopupState(popupState, product);
    }

    onUpgradeAction = () => {
        const {
            updateV2PopupState,
            showV2UI,
            onUpgrade,
            product,
            acknowledgeV1Popup
        } = this.props;
        showV2UI(true, product);
        updateV2PopupState(UI_SWICHER_STATES.NEW_UI, product);
        acknowledgeV1Popup(product);

        if (onUpgrade) {
            onUpgrade();
        }
    };

    onOpenNewView = () => {
        const { updateV2PopupState, showV2UI, onUpgrade, product } = this.props;
        showV2UI(true, product);
        updateV2PopupState(UI_SWICHER_STATES.NEW_UI, product);
        if (onUpgrade) {
            onUpgrade();
        }
    };

    getNewUIState() {
        const {
            updateV2PopupState,
            onDismiss,
            showV2UI,
            onRevert,
            product,
            t,
            acknowledgeV2Popup,
            isAccessWalkthroughAcknowledged,
            liveViewVisibilitySettings,
            isV1PopupAcknowledged,
            isGlobalPersonnel
        } = this.props;
        const popupTexts = getPopupTexts(
            product,
            t,
            liveViewVisibilitySettings,
            isGlobalPersonnel
        );
        return {
            acceptActionLabel: popupTexts.NEW_UI.acceptActionLabel,
            cancelActionLabel: popupTexts.NEW_UI.cancelActionLabel,
            title: popupTexts.NEW_UI.title,
            onAccept: () => {
                acknowledgeV2Popup(product);
                onDismiss();
            },
            onCancel: popupTexts.NEW_UI.cancelActionLabel
                ? () => {
                      if (
                          product === Products.DISPATCHER_PLANNER &&
                          !isGlobalPersonnel
                      ) {
                          onDismiss();
                          return;
                      }
                      showV2UI(false, product);
                      if (!isV1PopupAcknowledged) {
                          updateV2PopupState(
                              UI_SWICHER_STATES.JUST_REVERTED,
                              product
                          );
                      }
                      onRevert?.();
                  }
                : null,
            onDismiss: () => {
                if (
                    (product === Products.DISPATCHER_PLANNER &&
                        !isGlobalPersonnel) ||
                    isAccessWalkthroughAcknowledged
                ) {
                    onDismiss();
                    return;
                }
                updateV2PopupState(
                    UI_SWICHER_STATES.ACCESS_WALKTHROUGH_ANYTIME,
                    product
                );

                acknowledgeV2Popup(product);
            },
            Content: () => <p>{popupTexts.NEW_UI.content[0]}</p>
        };
    }

    getAccessWalkthroughState() {
        const {
            updateV2PopupState,
            onDismiss,
            product,
            t,
            acknowledgeAccessWalkthrough
        } = this.props;
        const popupTexts = getPopupTexts(product, t);
        return {
            acceptActionLabel:
                popupTexts.ACCESS_WALKTHROUGH_ANYTIME.acceptActionLabel,
            cancelActionLabel:
                popupTexts.ACCESS_WALKTHROUGH_ANYTIME.cancelActionLabel,
            title: popupTexts.ACCESS_WALKTHROUGH_ANYTIME.title,
            onAccept: null,
            onCancel: () => {
                onDismiss();
                acknowledgeAccessWalkthrough(product);
                updateV2PopupState(UI_SWICHER_STATES.NEW_UI, product);
            },
            onDismiss: () => {
                onDismiss();
                updateV2PopupState(UI_SWICHER_STATES.NEW_UI, product);
                acknowledgeAccessWalkthrough(product);
            },
            Content: () => (
                <p>{popupTexts.ACCESS_WALKTHROUGH_ANYTIME.content[0]}</p>
            )
        };
    }

    getManuallyRevertedState() {
        const { t, product } = this.props;
        const options = this.getNewUIState();
        const popupTexts = getPopupTexts(product, t);
        return {
            ...options,
            acceptActionLabel:
                popupTexts.MANUALLY_NAVIGATED_TO_OLD_UI.acceptActionLabel,
            onAccept: this.onOpenNewView
        };
    }

    getOldUIState() {
        const { onDismiss, product, t } = this.props;
        const popupTexts = getPopupTexts(product, t);
        return {
            acceptActionLabel: popupTexts.OLD_UI.acceptActionLabel,
            cancelActionLabel: popupTexts.OLD_UI.cancelActionLabel,
            title: popupTexts.OLD_UI.title,
            onAccept: this.onUpgradeAction,
            onCancel: onDismiss,
            onDismiss,
            Content: () => (
                <>
                    <p>{popupTexts.OLD_UI.content[0]}</p>
                    <p>{popupTexts.OLD_UI.content[1]}</p>
                </>
            )
        };
    }

    getJustRevertedState() {
        const {
            onDismiss,
            t,
            product,
            acknowledgeV1Popup,
            updateV2PopupState
        } = this.props;
        const popupTexts = getPopupTexts(product, t);
        const onDismissWrapper = () => {
            onDismiss();
            acknowledgeV1Popup(product);
            updateV2PopupState(UI_SWICHER_STATES.OLD_UI, product);
        };
        return {
            acceptActionLabel: popupTexts.JUST_REVERTED.acceptActionLabel,
            cancelActionLabel: popupTexts.JUST_REVERTED.cancelActionLabel,
            title: popupTexts.JUST_REVERTED.title,
            onAccept: this.onUpgradeAction,
            onCancel: onDismissWrapper,
            onDismiss: onDismissWrapper,
            Content: () => <p>{popupTexts.JUST_REVERTED.content[0]}</p>
        };
    }

    getDialogState(): IUpgradePopupState {
        let {
            v2PopupState: dialogState = UI_SWICHER_STATES.OLD_UI
        } = this.props;

        // Adding this check as the default value is ignored if the destructured value is null
        if (dialogState === null) {
            dialogState = UI_SWICHER_STATES.OLD_UI;
        }

        switch (dialogState) {
            case UI_SWICHER_STATES.TOUR_SKIPPED:
            case UI_SWICHER_STATES.JUST_UPGRADED:
            case UI_SWICHER_STATES.NEW_UI: {
                return this.getNewUIState();
            }
            case UI_SWICHER_STATES.OLD_UI: {
                return this.getOldUIState();
            }
            case UI_SWICHER_STATES.MANUALLY_NAVIGATED_TO_OLD_UI: {
                return this.getManuallyRevertedState();
            }
            case UI_SWICHER_STATES.JUST_REVERTED:
            case UI_SWICHER_STATES.DO_IT_LATER: {
                return this.getJustRevertedState();
            }
            case UI_SWICHER_STATES.ACCESS_WALKTHROUGH_ANYTIME: {
                return this.getAccessWalkthroughState();
            }
            default: {
                throw new Error('INVALID_STATE');
            }
        }
    }
    render() {
        const state = this.getDialogState();
        const {
            acceptActionLabel,
            cancelActionLabel,
            title,
            onAccept,
            onCancel,
            onDismiss,
            Content,
            disableAccept
        } = state;
        const { v2PopupState, product } = this.props;
        const popupTestId = `${product}-${UiSwitcherIds.uiSwitcherPopup}`;
        return (
            <Box data-test-id={popupTestId}>
                <Dialog
                    testId={v2PopupState}
                    theme={theme}
                    headerColor="uiSwitcherHeader"
                    className={theme.uiSwitchDialog}
                    dialogClassName={theme.uiSwitchDialogInner}
                    acceptActionLabel={acceptActionLabel}
                    cancelActionLabel={cancelActionLabel}
                    active
                    title={title}
                    onAccept={onAccept}
                    onCancel={onCancel}
                    onDismiss={onDismiss}
                    disableAccept={disableAccept}
                >
                    <Content />
                </Dialog>
            </Box>
        );
    }
}

const withRedux = connect<Props, OwnProps, _, _, _, _>(
    (state, props) => ({
        isV2UIActive: getV2UIStatus(state, props.product),
        v2PopupState: getV2PopupState(state, props.product),
        isAccessWalkthroughAcknowledged: getIsAccessWalkthroughAcknowledged(
            state,
            props.product
        ),
        liveViewVisibilitySettings: getLiveViewVisibilitySettings(state, props),
        isV1PopupAcknowledged: getIsV1PopupAcknowledged(state, props.product),
        isGlobalPersonnel: isGlobalPersonnelSelector(state, props)
    }),
    {
        showV2UI: AppPreferencesActions.showV2UI,
        updateV2PopupState: UISwitcherActions.updateV2PopupState,
        acknowledgeV2Popup: UISwitcherActions.acknowledgeV2Popup,
        acknowledgeV1Popup: UISwitcherActions.acknowledgeV1Popup,
        acknowledgeAccessWalkthrough:
            UISwitcherActions.acknowledgeAccessWalkthrough
    }
);

export default withRedux(translate()(UiSwitcherPopup));
